// Unpublished Work © 2022-2026 Deere & Company.

import React from 'react';
import {
    alphaNumericCompare,
    dateCompare,
    dueColumnCompare,
    formatDateSelect,
    genericServiceCompare, getInterval,
    getPercentClassName,
    getPercentDue,
    numericCompare
} from 'Ui/models/maintenance';
import EditLink from 'Ui/components/common/data-table/edit-link';
import OnLinkProgressBar from 'Ui/components/common/onlink-progress-bar';
import {Box, Tooltip} from '@deere/isg.component-library';
import {getStore} from 'Store/store';
import {RECOMMENDED} from 'Common/constants/service-status';
import {openDialog as openReduxDialog} from 'Store/actions/dialogs';
import dialogTypes from 'Ui/components/common/dialog-types';
import {formatNumberOrDefault} from 'OnEquip/equipment/utils/equipment-detail-formatters';

const DECIMAL_TO_PERCENT = 100;

export function getRows(servicePriority = [], translations) {
    const recommendedServices = servicePriority.filter((service) => service.status === RECOMMENDED);

    return recommendedServices.map((service) => {
        const partNumbers = service.partsList?.map((part) => part.partNumber) || [];

        return {
            ...service,
            interval: getInterval(service, translations),
            percentDue: getPercentDue(service),
            formattedAtDateSelect: formatDateSelect(service),
            formattedParts: partNumbers.join(', '),
            formattedAtHours: formatNumberOrDefault(service.atHours, 0)
        };
    });
}

export function getColumns(translations, updateEquipment) {
    return [
        {
            headerName: translations.INTERVAL,
            valueGetter: (_value, row) => row,
            field: 'interval',
            minWidth: 120,
            flex: 1,
            sortComparator: (a, b) => {
                return genericServiceCompare(a, b, alphaNumericCompare, 'interval');
            },
            renderCell: (params) => {
                return params.row.interval;
            }
        },
        {
            headerName: translations.ONLINK_SERVICE,
            valueGetter: (_value, row) => row,
            field: 'serviceTypeName',
            minWidth: 150,
            flex: 1,
            sortComparator: (a, b) => {
                return genericServiceCompare(a, b, alphaNumericCompare, 'serviceTypeName');
            },
            renderCell: (params) => {
                const {row: service} = params;

                return (
                    <EditLink
                        onClick={() => {
                            getStore().dispatch(openReduxDialog(dialogTypes.SERVICE_FORM_DIALOG, {
                                onSave: updateEquipment,
                                serviceId: service.serviceId,
                                translations
                            }));
                        }}
                    >
                        {service.serviceTypeName}
                    </EditLink>
                );
            }
        },
        {
            headerName: translations.ONLINK_PARTS_HEADER,
            valueGetter: (_value, row) => row,
            field: 'formattedParts',
            minWidth: 150,
            flex: 1,
            sortComparator: (a, b) => {
                return genericServiceCompare(a, b, alphaNumericCompare, 'formattedParts');
            },
            renderCell: (params) => {
                const {row: service} = params;

                return (
                    <Tooltip
                        placement='bottom'
                        title={service.formattedParts}
                    >
                        <span>
                            {service.formattedParts}
                        </span>
                    </Tooltip>
                );
            }
        },
        {
            headerName: translations.ONLINK_DUE_DATE,
            valueGetter: (_value, row) => row,
            field: 'formattedAtDateSelect',
            width: 120,
            sortComparator: (a, b) => {
                return genericServiceCompare(a, b, dateCompare, 'atDateSelect');
            },
            renderCell: (params) => {
                return params.row.formattedAtDateSelect ?? '';
            }
        },
        {
            headerName: translations.ONLINK_DUE_HOURS,
            valueGetter: (_value, row) => row,
            field: 'formattedAtHours',
            type: 'number',
            width: 150,
            sortComparator: (a, b) => {
                return genericServiceCompare(a, b, numericCompare, 'atHours');
            },
            renderCell: (params) => {
                return params.row.formattedAtHours;
            }
        },
        {
            headerName: translations.DUE,
            valueGetter: (_value, row) => row,
            field: 'dueProgressBar',
            className: 'center',
            width: 100,
            renderCell: (params) => {
                const {row: service} = params;
                const progress = service.percentDue;
                const progressClassName = getPercentClassName(service);

                return (
                    <Box
                        alignItems='center'
                        display='flex'
                        height='100%'
                        justifyContent='center'
                    >
                        <OnLinkProgressBar
                            barClassName='maintenance-progress-bar'
                            fillClassName={progressClassName}
                            percentFilled={Math.min(1, progress) * DECIMAL_TO_PERCENT}
                        />
                    </Box>
                );
            },
            sortComparator: dueColumnCompare
        }
    ];
}
